import type { ReactNode } from 'react';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import styled from '@emotion/styled';
import type { Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import { PROPERTY_PANEL_WIDTH } from 'containers/PropertyPanel/constants';

type SideCardInterface = {
  children?: ReactNode;
  expandValue: boolean;
  title: React.ReactNode;
  closeCard(): void;
  menu?: ReactNode;
};

export const SideCardWrapper = styled(motion.div)`
  width: 100%;
  box-sizing: border-box;
  z-index: 20;
  position: relative;
  max-width: min(1400px, calc(100vw - ${PROPERTY_PANEL_WIDTH}px));
`;

export const SideCartContainer = styled('div')`
  background-color: #fff;
  padding: 16px;
  min-width: 0;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${(p) => p.theme.palette.grey['200']};
  overflow: hidden;
`;

export const SideCardContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
`;

export const CardHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

export const CardTitle = styled('h1')`
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  margin: 0px;
  color: ${(p) => p.theme.palette.grey['800']};
`;

const HeaderActions = styled('div')`
  display: flex;
  align-items: center;
`;

export const SideCard = ({
  children,
  expandValue,
  title,
  closeCard,
  menu,
}: SideCardInterface) => {
  const variants = {
    open: {
      x: 0,
      width: '100%',
      maxWidth: 1400,
      display: 'flex',
      transition: {
        y: { stiffness: 1000, velocity: -100 },
      },
    },
    closed: {
      x: '100%',
      width: 0,
      maxWidth: 0,
    },
  };

  return (
    <SideCardWrapper
      animate={expandValue ? 'open' : 'closed'}
      variants={variants as Variants}
      initial={{ maxWidth: 0 }}
    >
      {expandValue && (
        <SideCartContainer>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            <HeaderActions>
              {menu && menu}
              <IconButton
                aria-label='Close'
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  closeCard();
                }}
              >
                <CloseIcon />
              </IconButton>
            </HeaderActions>
          </CardHeader>
          <SideCardContent>{children}</SideCardContent>
        </SideCartContainer>
      )}
    </SideCardWrapper>
  );
};
